import React from 'react';
import { EyeOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { Col, Row, Tooltip } from 'antd';
import { Box, Text } from 'atoms';
import { ACL } from 'components/common';

// Custom Delete Icon SVG Component
function CustomDeleteIcon() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<rect x="0.5" y="0.5" width="15" height="15" rx="1.5" fill="white" />
			<rect x="0.5" y="0.5" width="15" height="15" rx="1.5" stroke="#FF3B30" />
			<path
				d="M4.66669 10.256V5.74268C4.66669 5.44668 4.77224 5.19335 4.98335 4.98268C5.19446 4.77202 5.4478 4.66646 5.74335 4.66602H10.2567C10.5527 4.66602 10.806 4.77157 11.0167 4.98268C11.2274 5.19379 11.3329 5.44713 11.3334 5.74268V10.256C11.3334 10.552 11.2278 10.8053 11.0167 11.016C10.8056 11.2267 10.5522 11.3322 10.2567 11.3327H5.74335C5.44735 11.3327 5.19402 11.2271 4.98335 11.016C4.77269 10.8049 4.66713 10.5511 4.66669 10.256Z"
				fill="#FF3B30"
			/>
		</svg>
	);
}

const DateFormat = 'DD-MM-YYYY';
const TIME_FORMAT = 'hh:mm a';

function getLocalDate(date) {
	if (date) {
		return moment(date).add(330, 'minutes').format(DateFormat);
	}
	return '';
}

function getLocalTime(time) {
	if (time) {
		return moment(time).add(330, 'minutes').format(TIME_FORMAT);
	}
	return '';
}

// V2 backend already returns dates in IST — no offset needed
function getDirectDate(date) {
	if (date) {
		return moment(date).format(DateFormat);
	}
	return '';
}

function getDirectTime(time) {
	if (time) {
		return moment(time).format(TIME_FORMAT);
	}
	return '';
}

function getValidType(valid) {
	return valid === true ? 'Valid' : 'Invalid';
}

function getProjectNameColumn(isV2) {
	return {
		title: 'Project Name',
		dataIndex: isV2 ? 'project_name' : 'project_location_code',
		key: 'project_name',
		width: 90,
		hidden: false,
		render: (value, record) => {
			// For old UI, use project_location_code; for new UI, use project_name
			const projectValue = isV2 ? record?.project_name : record?.project_location_code;
			return (
				<Text overFlow="hidden" style={{ minWidth: '70px' }}>
					{projectValue || ''}
				</Text>
			);
		},
	};
}

export const IntercityTripTableConfig = (onDelete, isV2 = true) => [
	{
		title: 'Trip ID',
		dataIndex: 'id',
		key: 'id',
		hidden: false,
		width: 60,
		render: (id) => <Text overFlow="hidden">{id || ''}</Text>,
	},
	{
		title: 'Adhoc Request ID',
		dataIndex: 'adhoc_request_id',
		key: 'adhoc_request_id',
		hidden: false,
		width: 90,
		render: (adhoc_request_id) => (
			<Text overFlow="hidden" style={{ minWidth: '70px' }}>
				{adhoc_request_id || '-'}
			</Text>
		),
	},
	getProjectNameColumn(isV2),
	{
		title: 'City',
		dataIndex: 'city',
		key: 'city',
		width: 70,
		hidden: false,
		render: (city) => <Text overFlow="hidden">{city || ''}</Text>,
	},
	{
		title: 'Client',
		dataIndex: 'client_name',
		key: 'client_name',
		width: 90,
		hidden: false,
		render: (client_name) => (
			<Text overFlow="hidden" style={{ minWidth: '70px' }}>
				{client_name || ''}
			</Text>
		),
	},
	{
		title: 'Vendor',
		dataIndex: 'vendor_name',
		key: 'vendor_name',
		width: 90,
		hidden: false,
		render: (vendor_name) => (
			<Text overFlow="hidden" style={{ minWidth: '70px' }}>
				{vendor_name || ''}
			</Text>
		),
	},
	{
		title: 'Vehicle No.',
		dataIndex: 'vehicle_number',
		key: 'vehicle_number',
		width: 80,
		hidden: false,
		render: (vehicle_number) => (
			<Text overFlow="hidden" style={{ minWidth: '60px' }}>
				{vehicle_number || ''}
			</Text>
		),
	},
	{
		title: 'Reporting',
		dataIndex: 'reporting_time',
		key: 'reporting_time',
		width: 100,
		hidden: false,
		render: (reporting_time, record) => {
			let reportingDateTime = null;
			if (isV2 && record?.actual_reporting_time) {
				reportingDateTime = record.actual_reporting_time;
			} else if (!isV2 && record?.reporting_time) {
				// V1: reporting_time carries the actual date+time, while reporting_date
				// is date-only (midnight). Use reporting_time so the +5:30 offset is
				// applied to the real reporting time instead of 00:00.
				reportingDateTime = record.reporting_time;
			} else if (record?.reporting_date) {
				// Root-level reporting_date (V2 sends date+time combined or date only)
				reportingDateTime = record.reporting_date;
			} else if (record?.info?.reporting_date && record?.info?.reporting_time) {
				// Combine date and time from info object
				reportingDateTime = `${record.info.reporting_date} ${record.info.reporting_time}`;
			} else if (record?.info?.reporting_date) {
				// Only date available from info
				reportingDateTime = record.info.reporting_date;
			} else if (record?.reporting_time) {
				// Fallback to root level reporting_time
				reportingDateTime = record.reporting_time;
			} else if (reporting_time) {
				// Fallback to reporting_time parameter
				reportingDateTime = reporting_time;
			}

			const formatDate = isV2 ? getDirectDate : getLocalDate;
			const formatTime = isV2 ? getDirectTime : getLocalTime;

			return (
				<Box>
					<Text overFlow="hidden" style={{ minWidth: '60px' }}>
						{reportingDateTime ? formatDate(reportingDateTime) : '-'}
					</Text>
					<Text overFlow="hidden" style={{ minWidth: '60px' }}>
						{reportingDateTime ? formatTime(reportingDateTime) : ''}
					</Text>
				</Box>
			);
		},
	},
	{
		title: 'Start',
		dataIndex: 'start_time',
		key: 'start_time',
		width: 100,
		hidden: false,
		render: (start_time, record) => {
			const formatDate = isV2 ? getDirectDate : getLocalDate;
			const formatTime = isV2 ? getDirectTime : getLocalTime;
			const targetStartVal =
				isV2 && record?.actual_start_time
					? record.actual_start_time
					: record?.start_time || start_time;
			return (
				<Box>
					<Text overFlow="hidden" style={{ minWidth: '60px' }}>
						{targetStartVal ? formatDate(targetStartVal) : '-'}
					</Text>
					<Text overFlow="hidden" style={{ minWidth: '60px' }}>
						{targetStartVal ? formatTime(targetStartVal) : ''}
					</Text>
				</Box>
			);
		},
	},
	{
		title: 'Run Time',
		dataIndex: 'trip_duration_hours',
		key: 'trip_duration_hours',
		width: 70,
		hidden: false,
		render: (trip_duration_hours) => {
			if (trip_duration_hours == null || trip_duration_hours === '') return '-';
			const duration = parseFloat(trip_duration_hours);
			if (Number.isNaN(duration)) return '-';
			const hours = Math.floor(duration);
			const minutes = Math.round((duration - hours) * 60);
			return (
				<Text overFlow="hidden" style={{ minWidth: '40px' }}>
					{`${hours} hrs ${minutes} mins`}
				</Text>
			);
		},
	},
	{
		title: 'Source',
		dataIndex: 'source_entry',
		key: 'source_entry',
		width: 70,
		hidden: false,
		render: (source_entry) => (
			<Box>
				<Text overFlow="hidden">{source_entry}</Text>
			</Box>
		),
	},
	{
		title: 'Valid',
		dataIndex: 'is_valid',
		key: 'is_valid',
		width: 60,
		hidden: false,
		render: (is_valid) => (
			<Box>
				<Text overFlow="hidden">{getValidType(is_valid)}</Text>
			</Box>
		),
	},
	{
		title: 'Action',
		dataIndex: 'id',
		key: 'id',
		hidden: false,
		width: 90,
		render: (id, record) => {
			const location = typeof window !== 'undefined' ? window.location : { pathname: '' };
			return (
				<Row gutter={2} justify="space-between">
					<Col>
						<Link
							to={{ pathname: `/trips/${id}/overview` }}
							state={{ from: location.pathname }}
						>
							<EyeOutlined />
						</Link>
					</Col>
					<Col>
						<Tooltip title="End Trip">
							<ACL action="update" module="trip">
								<button
									type="button"
									style={{
										cursor: 'pointer',
										display: 'inline-block',
										background: 'none',
										border: 'none',
										padding: 0,
										margin: 0,
									}}
									onClick={() => onDelete(record)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											onDelete(record);
										}
									}}
									aria-label="End Trip"
								>
									<CustomDeleteIcon />
								</button>
							</ACL>
						</Tooltip>
					</Col>
				</Row>
			);
		},
	},
];
